// Supabase Service for English Daily Application
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://eximyshafkuyehkiotuf.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4aW15c2hhZmt1eWVoa2lvdHVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxODc0MDEsImV4cCI6MjA3MDc2MzQwMX0.bjrI5MO_WUfnI53UaQ_m8BS8LPh2et5OrqMKmWUvYzw';

const supabase = createClient(supabaseUrl, supabaseKey);

// ESS Toolbox Service
export const essToolboxService = {
  // Get all ESS Toolbox data
  async getAllData() {
    try {
      const { data, error } = await supabase
        .from('eng_esstoolbox')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for ESS Toolbox');
        const { essToolboxData } = await import('../data/essToolboxData');
        return essToolboxData;
      }
      
      return this.transformESSData(data);
    } catch (error) {
      console.error('Error fetching ESS Toolbox data:', error);
      // Fallback to mock data on error
      const { essToolboxData } = await import('../data/essToolboxData');
      return essToolboxData;
    }
  },

  // Get DeepSeek story list for ESS Toolbox
  async getDeepSeekStories() {
    try {
      const { data, error } = await supabase
        .from('eng_esstoolbox')
        .select('*')
        .eq('data_type', 'deepseek_story')
        .order('id', { ascending: true });

      if (error) throw error;

      return (data || []).map(item => {
        const raw = item.raw || null;
        return ({
          id: item.story_id || item.content_id || String(item.id),
        title: item.title,
        category: item.category,
        difficulty: item.difficulty,
          content: item.content, // passage text
          raw: raw ? raw : {
            analyses: item.analyses,
            visualization: item.visualization,
            quiz: item.quiz,
            rewrite: item.rewrite,
            stats: item.stats
          }
        });
      });
    } catch (error) {
      console.error('Error fetching ESS DeepSeek stories:', error);
      return [];
    }
  },

  // Save/Upsert a DeepSeek story into eng_esstoolbox
  async saveDeepSeekStory({ storyId, title, category, difficulty, content, raw }) {
    try {
      const clip = (s, max) => (s == null ? null : String(s).slice(0, max));
      const TITLE_MAX = 500;
      const CID_MAX = 100;  // content_id varchar(100)
      const CAT_MAX = 200;
      const DIF_MAX = 200;

      // Start with the richest payload; if the table is missing columns
      // (e.g., story_id/category/difficulty/raw/updated_at), we progressively
      // remove the missing columns and retry until insert succeeds.
      let candidate = {
        data_type: 'deepseek_story',
        content_id: clip(storyId, CID_MAX) || null,
        title: clip(title, TITLE_MAX),
        category: clip(category, CAT_MAX) || null,
        difficulty: clip(difficulty, DIF_MAX) || null,
        content: content || null,
        raw: raw || null,
        updated_at: new Date().toISOString()
      };

      const tryInsert = async () => {
        const { data, error } = await supabase
          .from('eng_esstoolbox')
          .insert([candidate])
          .select();
        if (!error) return data?.[0] || null;

        const msg = String(error.message || '').toLowerCase();
        // Strip the first missing column and retry
        if (msg.includes('column') && msg.includes('content_id')) { delete candidate.content_id; return tryInsert(); }
        if (msg.includes('column') && msg.includes('category')) {
          delete candidate.category; return tryInsert();
        }
        if (msg.includes('column') && msg.includes('difficulty')) {
          delete candidate.difficulty; return tryInsert();
        }
        if (msg.includes('column') && msg.includes('raw')) {
          delete candidate.raw; return tryInsert();
        }
        if (msg.includes('column') && msg.includes('updated_at')) {
          delete candidate.updated_at; return tryInsert();
        }

        // If it's a different error, surface it
        throw error;
      };

      return await tryInsert();
    } catch (error) {
      console.error('Error saving ESS DeepSeek story:', error);
      throw error;
    }
  },

  // Get sample passages
  async getSamplePassages() {
    try {
      const { data, error } = await supabase
        .from('eng_esstoolbox')
        .select('*')
        .eq('data_type', 'sample_passage')
        .order('id', { ascending: true });
      
      if (error) throw error;
      return data.map(item => ({
        id: item.content_id,
        title: item.title,
        content: item.content
      }));
    } catch (error) {
      console.error('Error fetching sample passages:', error);
      throw error;
    }
  },

  // Get sentence patterns
  async getSentencePatterns() {
    try {
      const { data, error } = await supabase
        .from('eng_esstoolbox')
        .select('*')
        .eq('data_type', 'sentence_pattern')
        .order('id', { ascending: true });
      
      if (error) throw error;
      return data.map(item => ({
        [item.content_id]: {
          name: item.english_name,
          chineseName: item.chinese_name,
          description: item.description,
          examples: item.examples
        }
      })).reduce((acc, curr) => ({ ...acc, ...curr }), {});
    } catch (error) {
      console.error('Error fetching sentence patterns:', error);
      throw error;
    }
  },

  // Transform data to match original structure
  transformESSData(data) {
    const samplePassages = data
      .filter(item => item.data_type === 'sample_passage')
      .map(item => ({
        id: item.content_id,
        title: item.title,
        content: item.content
      }));

    const sentencePatterns = data
      .filter(item => item.data_type === 'sentence_pattern')
      .reduce((acc, item) => {
        acc[item.content_id] = {
          name: item.english_name,
          chineseName: item.chinese_name,
          description: item.description,
          examples: item.examples
        };
        return acc;
      }, {});

    return {
      samplePassages,
      sentencePatterns,
      mockQuizQuestions: [], // Will be populated from other tables
      mockRewriteExercises: [], // Will be populated from other tables
      structureAnalysisTemplates: {}, // Will be populated from other tables
      treeVisualizationData: [] // Will be populated from other tables
    };
  }
};

// HKDSE Grammar Service
export const hkdseGrammarService = {
  // Get all grammar categories
  async getGrammarCategories() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'grammar_category')
        .is('parent_id', null)
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for grammar categories');
        const { grammarCategories } = await import('../data/hkdseGrammarData');
        return grammarCategories;
      }
      
      return data.reduce((acc, item) => {
        acc[item.content_id] = {
          id: item.content_id,
          title: item.title,
          icon: item.icon,
          color: item.color,
          description: item.description,
          hkdseRelevance: item.hkdse_relevance
        };
        return acc;
      }, {});
    } catch (error) {
      console.error('Error fetching grammar categories:', error);
      // Fallback to mock data on error
      const { grammarCategories } = await import('../data/hkdseGrammarData');
      return grammarCategories;
    }
  },

  // Get parts of speech data
  async getPartsOfSpeechData() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'parts_of_speech')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for parts of speech');
        const { partsOfSpeechData } = await import('../data/hkdseGrammarData');
        return partsOfSpeechData;
      }
      
      return this.transformPartsOfSpeechData(data);
    } catch (error) {
      console.error('Error fetching parts of speech data:', error);
      // Fallback to mock data on error
      const { partsOfSpeechData } = await import('../data/hkdseGrammarData');
      return partsOfSpeechData;
    }
  },

  // Get verb tenses data
  async getVerbTensesData() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'verb_tenses')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for verb tenses');
        const { verbTensesData } = await import('../data/hkdseGrammarData');
        return verbTensesData;
      }
      
      return this.transformVerbTensesData(data);
    } catch (error) {
      console.error('Error fetching verb tenses data:', error);
      // Fallback to mock data on error
      const { verbTensesData } = await import('../data/hkdseGrammarData');
      return verbTensesData;
    }
  },

  // Get sentence structure data
  async getSentenceStructureData() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'sentence_structure')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for sentence structure');
        const { sentenceStructureData } = await import('../data/hkdseGrammarData');
        return sentenceStructureData;
      }
      
      return this.transformSentenceStructureData(data);
    } catch (error) {
      console.error('Error fetching sentence structure data:', error);
      // Fallback to mock data on error
      const { sentenceStructureData } = await import('../data/hkdseGrammarData');
      return sentenceStructureData;
    }
  },

  // Get clauses and phrases data
  async getClausesPhrasesData() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'clauses_phrases')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for clauses and phrases');
        const { clausesPhrasesData } = await import('../data/hkdseGrammarData');
        return clausesPhrasesData;
      }
      
      return this.transformClausesPhrasesData(data);
    } catch (error) {
      console.error('Error fetching clauses and phrases data:', error);
      // Fallback to mock data on error
      const { clausesPhrasesData } = await import('../data/hkdseGrammarData');
      return clausesPhrasesData;
    }
  },

  // Get common errors data
  async getCommonErrorsData() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'common_errors')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for common errors');
        const { commonErrorsData } = await import('../data/hkdseGrammarData');
        return commonErrorsData;
      }
      
      return this.transformCommonErrorsData(data);
    } catch (error) {
      console.error('Error fetching common errors data:', error);
      // Fallback to mock data on error
      const { commonErrorsData } = await import('../data/hkdseGrammarData');
      return commonErrorsData;
    }
  },

  // Get practice exercises data
  async getPracticeExercisesData() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'practice_exercises')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for practice exercises');
        const { practiceExercisesData } = await import('../data/hkdseGrammarData');
        return practiceExercisesData;
      }
      
      return this.transformPracticeExercisesData(data);
    } catch (error) {
      console.error('Error fetching practice exercises data:', error);
      // Fallback to mock data on error
      const { practiceExercisesData } = await import('../data/hkdseGrammarData');
      return practiceExercisesData;
    }
  },

  // Get HKDSE exam tips
  async getHKDSEExamTips() {
    try {
      const { data, error } = await supabase
        .from('eng_hkdsegrammar')
        .select('*')
        .eq('data_type', 'exam_tip')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for HKDSE exam tips');
        const { hkdseExamTips } = await import('../data/hkdseGrammarData');
        return hkdseExamTips;
      }
      
      return this.transformHKDSEExamTips(data);
    } catch (error) {
      console.error('Error fetching HKDSE exam tips:', error);
      // Fallback to mock data on error
      const { hkdseExamTips } = await import('../data/hkdseGrammarData');
      return hkdseExamTips;
    }
  },

  // Transform parts of speech data
  transformPartsOfSpeechData(data) {
    const result = {};
    
    // First, group data by parent_id to create the main categories
    const mainCategories = data.filter(item => item.parent_id === 'parts-of-speech');
    const subtypes = data.filter(item => item.parent_id && item.parent_id !== 'parts-of-speech');
    
    // Create main categories
    mainCategories.forEach(item => {
      result[item.content_id] = {
        id: item.content_id,
        title: item.title,
        chineseTitle: item.chinese_title,
        definition: item.definition,
        types: []
      };
    });
    
    // Add subtypes to their parent categories
    subtypes.forEach(item => {
      const parentId = item.parent_id;
      if (result[parentId]) {
        result[parentId].types.push({
          type: item.content_id.replace(`${parentId}-`, '').replace('-', '_'),
          name: item.title,
          chineseName: item.chinese_title,
          definition: item.definition,
          examples: item.examples || [],
          rules: item.rules || [],
          hkdseNote: item.hkdse_note
        });
      }
    });
    
    return result;
  },

  // Transform verb tenses data
  transformVerbTensesData(data) {
    // Find timeline data
    const timelineItem = data.find(item => item.content_id === 'timeline');
    const timeline = timelineItem ? timelineItem.timeline : {
      past: ['Past Perfect', 'Past Continuous', 'Simple Past'],
      present: ['Present Perfect', 'Present Continuous', 'Simple Present'],
      future: ['Future Perfect', 'Future Continuous', 'Simple Future']
    };
    
    // Get tense data (exclude timeline)
    const tenseData = data.filter(item => item.content_id !== 'timeline');

    const tenses = tenseData.map(item => ({
      id: item.content_id,
      name: item.title,
      chineseName: item.chinese_title,
      structure: item.structure,
      examples: item.examples || [],
      usage: item.usage || [],
      timeMarkers: item.time_markers || [],
      hkdseNote: item.hkdse_note,
      commonErrors: item.common_errors || []
    }));

    return { timeline, tenses };
  },

  // Transform sentence structure data
  transformSentenceStructureData(data) {
    // Group data by content_id patterns
    const basicPatterns = data
      .filter(item => ['sv', 'svo', 'svc', 'svoo', 'svoc'].includes(item.content_id))
      .map(item => ({
        id: item.content_id,
        pattern: item.pattern,
        name: item.title,
        chineseName: item.chinese_title,
        description: item.description,
        examples: item.examples || [],
        hkdseNote: item.hkdse_note
      }));

    const sentenceTypes = data
      .filter(item => ['simple-sentence', 'compound-sentence', 'complex-sentence'].includes(item.content_id))
      .map(item => ({
        id: item.content_id,
        name: item.title,
        chineseName: item.chinese_title,
        definition: item.definition,
        structure: item.structure,
        examples: item.examples || [],
        connectors: item.connectors || [],
        subordinators: item.subordinators || [],
        hkdseNote: item.hkdse_note
      }));

    const voiceTypes = data
      .filter(item => ['active-voice', 'passive-voice'].includes(item.content_id))
      .map(item => ({
        id: item.content_id,
        name: item.title,
        chineseName: item.chinese_title,
        definition: item.definition,
        structure: item.structure,
        examples: item.examples || [],
        advantages: item.advantages || [],
        uses: item.uses || [],
        hkdseNote: item.hkdse_note
      }));

    return { basicPatterns, sentenceTypes, voiceTypes };
  },

  // Transform clauses and phrases data
  transformClausesPhrasesData(data) {
    // Group by parent_id to organize clauses and phrases
    const mainClauses = data.filter(item => item.parent_id === 'clauses-phrases');
    const subClauses = data.filter(item => item.parent_id && item.parent_id !== 'clauses-phrases');
    
    const clauses = mainClauses.map(item => ({
      id: item.content_id,
      name: item.title,
      chineseName: item.chinese_title,
      definition: item.definition,
      structure: item.structure,
      examples: item.examples || [],
      characteristics: item.characteristics || [],
      types: item.types || [],
      hkdseNote: item.hkdse_note,
      subtypes: subClauses
        .filter(sub => sub.parent_id === item.content_id)
        .map(sub => ({
          id: sub.content_id,
          name: sub.title,
          chineseName: sub.chinese_title,
          definition: sub.definition,
          structure: sub.structure,
          examples: sub.examples || [],
          hkdseNote: sub.hkdse_note
        }))
    }));

    return { clauses, phrases: [] }; // Phrases can be added later if needed
  },

  // Transform common errors data
  transformCommonErrorsData(data) {
    return data.reduce((acc, item) => {
      acc[item.content_id] = {
        id: item.content_id,
        title: item.title,
        description: item.description,
        rules: item.rules || [],
        commonMistakes: item.common_mistakes || [],
        hkdseNote: item.hkdse_note
      };
      return acc;
    }, {});
  },

  // Transform practice exercises data
  transformPracticeExercisesData(data) {
    return data.map(item => ({
      id: item.content_id,
      type: item.content_id.split('-')[0], // Extract type from content_id
      title: item.title,
      instruction: item.instruction,
      questions: item.questions || [],
      hkdseRelevance: item.hkdse_relevance
    }));
  },

  // Transform HKDSE exam tips
  transformHKDSEExamTips(data) {
    return data.reduce((acc, item) => {
      acc[item.content_id] = {
        title: item.title,
        tips: item.tips || [],
        grammarFocus: item.grammar_focus || []
      };
      return acc;
    }, {});
  }
};

// Knowledge Graph Service
export const knowledgeGraphService = {
  // Get all knowledge graph stories
  async getAllStories() {
    try {
      const { data, error } = await supabase
        .from('eng_knowledgegraph')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for knowledge graph');
        const { knowledgeGraphSamples } = await import('../data/knowledgeGraphData');
        return knowledgeGraphSamples;
      }
      
      return data.map(item => ({
        id: item.story_id,
        title: item.title,
        content: item.content,
        category: item.category,
        difficulty: item.difficulty,
        themes: item.themes,
        entities: item.entities,
        relationships: item.relationships,
        analysis: item.analysis
      }));
    } catch (error) {
      console.error('Error fetching knowledge graph stories:', error);
      // Fallback to mock data on error
      const { knowledgeGraphSamples } = await import('../data/knowledgeGraphData');
      return knowledgeGraphSamples;
    }
  },

  // Get story by ID
  async getStoryById(storyId) {
    try {
      const { data, error } = await supabase
        .from('eng_knowledgegraph')
        .select('*')
        .eq('story_id', storyId)
        .single();
      
      if (error) throw error;
      return {
        id: data.story_id,
        title: data.title,
        content: data.content,
        category: data.category,
        difficulty: data.difficulty,
        themes: data.themes,
        entities: data.entities,
        relationships: data.relationships,
        analysis: data.analysis
      };
    } catch (error) {
      console.error('Error fetching story by ID:', error);
      throw error;
    }
  },

  // Get stories by category
  async getStoriesByCategory(category) {
    try {
      const { data, error } = await supabase
        .from('eng_knowledgegraph')
        .select('*')
        .eq('category', category)
        .order('id', { ascending: true });
      
      if (error) throw error;
      return data.map(item => ({
        id: item.story_id,
        title: item.title,
        content: item.content,
        category: item.category,
        difficulty: item.difficulty,
        themes: item.themes,
        entities: item.entities,
        relationships: item.relationships,
        analysis: item.analysis
      }));
    } catch (error) {
      console.error('Error fetching stories by category:', error);
      throw error;
    }
  },

  // Get stories by difficulty
  async getStoriesByDifficulty(difficulty) {
    try {
      const { data, error } = await supabase
        .from('eng_knowledgegraph')
        .select('*')
        .eq('difficulty', difficulty)
        .order('id', { ascending: true });
      
      if (error) throw error;
      return data.map(item => ({
        id: item.story_id,
        title: item.title,
        content: item.content,
        category: item.category,
        difficulty: item.difficulty,
        themes: item.themes,
        entities: item.entities,
        relationships: item.relationships,
        analysis: item.analysis
      }));
    } catch (error) {
      console.error('Error fetching stories by difficulty:', error);
      throw error;
    }
  }
};

// Linguistic Analysis Service
export const linguisticAnalysisService = {
  // Get all linguistic analysis examples
  async getAllExamples() {
    try {
      const { data, error } = await supabase
        .from('eng_linguisticanalysis')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) throw error;
      
      // If no data or insufficient data, fallback to mock data
      if (!data || data.length === 0) {
        console.log('No Supabase data found, using mock data for linguistic analysis');
        const { linguisticAnalysisExamples } = await import('../data/linguisticAnalysisData');
        return linguisticAnalysisExamples;
      }
      
      return data.map(item => ({
        id: item.example_id,
        title: item.title,
        originalSentence: item.original_sentence,
        analysis: {
          mainPattern: item.main_pattern,
          basicStructure: item.basic_structure,
          legendStructure: item.legend_structure,
          detailedAnalysis: {
            subject: item.subject,
            verb: item.verb,
            directObject: item.direct_object,
            indirectObject: item.indirect_object,
            complement: item.complement,
            adverbial: item.adverbial
          },
          subordinateClauses: item.subordinate_clauses || [],
          verbTense: item.verb_tense,
          specialFeatures: item.special_features || [],
          rewriteOptions: item.rewrite_options || {}
        }
      }));
    } catch (error) {
      console.error('Error fetching linguistic analysis examples:', error);
      // Fallback to mock data on error
      const { linguisticAnalysisExamples } = await import('../data/linguisticAnalysisData');
      return linguisticAnalysisExamples;
    }
  },

  // Get example by ID
  async getExampleById(exampleId) {
    try {
      const { data, error } = await supabase
        .from('eng_linguisticanalysis')
        .select('*')
        .eq('example_id', exampleId)
        .single();
      
      if (error) throw error;
      return {
        id: data.example_id,
        title: data.title,
        originalSentence: data.original_sentence,
        analysis: {
          mainPattern: data.main_pattern,
          basicStructure: data.basic_structure,
          legendStructure: data.legend_structure,
          detailedAnalysis: {
            subject: data.subject,
            verb: data.verb,
            directObject: data.direct_object,
            indirectObject: data.indirect_object,
            complement: data.complement,
            adverbial: data.adverbial
          },
          subordinateClauses: data.subordinate_clauses || [],
          verbTense: data.verb_tense,
          specialFeatures: data.special_features || [],
          rewriteOptions: data.rewrite_options || {}
        }
      };
    } catch (error) {
      console.error('Error fetching linguistic analysis example by ID:', error);
      throw error;
    }
  }
};

export default supabase;
